import { mdiChevronLeftCircle, mdiChevronRightCircle, mdiCloseCircle } from '@mdi/js';
import Icon from '@mdi/react';
import React, { useEffect, useRef, useState } from 'react';
import { IMedia } from '../../interfaces/story';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";

interface IProps {
    close:Function
    images: IMedia[]
    mainIndex?: number
}

const SWIPE_THRESHOLD = 50;
const TAP_MOVE_THRESHOLD = 10;
const DEBUG_FULLSCREEN_VIEWER = window.__RUNTIME_CONFIG__.REACT_APP_DEBUG_VIEW === 'true';

export default function ImageFullscreenViewer(props:IProps) {

    const [mainIndex, setMainIndex ] = useState<number>(0);
    const touchStartRef = useRef<{ x: number, y: number, scale: number } | null>(null);
    const tapStartPosRef = useRef<{ x: number, y: number } | null>(null);
    const multiTouchRef = useRef<boolean>(false);
    const baselineScaleRef = useRef<number>(1);
    const [showControls, setShowControls] = useState<boolean>(true);
    // Debug overlay showing touch/swipe/zoom info, enabled via REACT_APP_DEBUG_VIEW
    const [debug, setDebug] = useState<{ rest?: string, start?: string, end?: string }>({});

    useEffect(() => {
        if(props.mainIndex) {
            setMainIndex(props.mainIndex)
        }
    }, []);

    useEffect(() => {
        const vv = window.visualViewport;
        if (vv) {
            baselineScaleRef.current = vv.scale;
        }
    }, []);

    useEffect(() => {
        if (!DEBUG_FULLSCREEN_VIEWER) {
            return;
        }
        const vv = window.visualViewport;
        if (!vv) {
            setDebug(prev => ({ ...prev, rest: 'no visualViewport' }));
            return;
        }
        const update = () => setDebug(prev => ({ ...prev, rest: `scale=${vv.scale.toFixed(4)}` }));
        update();
        vv.addEventListener('resize', update);
        vv.addEventListener('scroll', update);
        return () => {
            vv.removeEventListener('resize', update);
            vv.removeEventListener('scroll', update);
        };
    }, []);

    useEffect(() => {
        const onGlobalTouchStart = (e: TouchEvent) => {
            if (e.touches.length > 1) {
                multiTouchRef.current = true;
            }
        };
        document.addEventListener('touchstart', onGlobalTouchStart, { passive: true });
        return () => {
            document.removeEventListener('touchstart', onGlobalTouchStart);
        };
    }, []);

    const goToNext = () => {
        setMainIndex(prev => prev === props.images.length - 1 ? 0 : prev + 1);
    };

    const goToPrev = () => {
        setMainIndex(prev => prev === 0 ? props.images.length - 1 : prev - 1);
    };

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        if (e.touches.length > 1) {
            multiTouchRef.current = true;
        } else if (e.touches.length === 1) {
            multiTouchRef.current = false;
        }

        const vv = window.visualViewport;
        const scale = vv ? vv.scale : NaN;
        const zoomed = !!vv && scale > baselineScaleRef.current + 0.01;
        if (DEBUG_FULLSCREEN_VIEWER) {
            const tracking = e.touches.length === 1 && !zoomed;
            setDebug(prev => ({ ...prev, start: `touches=${e.touches.length} scale=${scale.toFixed(4)} baseline=${baselineScaleRef.current.toFixed(4)} zoomed=${zoomed} tracking=${tracking}`, end: undefined }));
        }
        if (e.touches.length === 1 && !zoomed) {
            touchStartRef.current = { x: e.touches[0].pageX, y: e.touches[0].pageY, scale };
        } else {
            touchStartRef.current = null;
        }

        const target = e.target as HTMLElement;
        const isArrowTarget = !!target.closest('.left, .right');

        if (e.touches.length === 1 && !isArrowTarget) {
            tapStartPosRef.current = { x: e.touches[0].pageX, y: e.touches[0].pageY };
        } else {
            tapStartPosRef.current = null;
        }
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        if (e.touches.length > 1) {
            multiTouchRef.current = true;
            touchStartRef.current = null;
            tapStartPosRef.current = null;
        } else if (e.touches.length === 1 && tapStartPosRef.current) {
            const dx = e.touches[0].pageX - tapStartPosRef.current.x;
            const dy = e.touches[0].pageY - tapStartPosRef.current.y;
            if (Math.abs(dx) > TAP_MOVE_THRESHOLD || Math.abs(dy) > TAP_MOVE_THRESHOLD) {
                tapStartPosRef.current = null;
            }
        }
    };

    const handleTouchEndOrCancel = (e: React.TouchEvent<HTMLDivElement>) => {
        if (multiTouchRef.current) {
            tapStartPosRef.current = null;
            touchStartRef.current = null;
            return;
        }

        if (tapStartPosRef.current && e.touches.length === 0) {
            setShowControls(prev => !prev);
        }
        tapStartPosRef.current = null;

        const start = touchStartRef.current;
        touchStartRef.current = null;
        if (!start || e.changedTouches.length === 0) {
            if (DEBUG_FULLSCREEN_VIEWER) {
                setDebug(prev => ({ ...prev, end: 'no tracked start' }));
            }
            return;
        }

        const vv = window.visualViewport;
        const currentScale = vv ? vv.scale : NaN;
        const zoomChanged = !isNaN(start.scale) && !isNaN(currentScale) && Math.abs(currentScale - start.scale) > 0.01;
        if (zoomChanged) {
            if (DEBUG_FULLSCREEN_VIEWER) {
                setDebug(prev => ({ ...prev, end: `zoom changed ${start.scale.toFixed(2)} -> ${currentScale.toFixed(2)} - ignoring` }));
            }
            return;
        }

        const deltaX = e.changedTouches[0].pageX - start.x;
        const deltaY = e.changedTouches[0].pageY - start.y;

        let action = 'none';

        if (Math.abs(deltaX) > SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
            if (deltaX < 0) {
                action = 'NEXT';
                goToNext();
            } else {
                action = 'PREV';
                goToPrev();
            }
        } else if (Math.abs(deltaY) > SWIPE_THRESHOLD && Math.abs(deltaY) > Math.abs(deltaX)) {
            action = 'CLOSE';
            props.close();
        }

        if (DEBUG_FULLSCREEN_VIEWER) {
            setDebug(prev => ({ ...prev, end: `dx=${deltaX.toFixed(0)} dy=${deltaY.toFixed(0)} action=${action}` }));
        }
    };

    const customRenderThumb = (children:any) =>
        children.map((item:any) => {
            // return <VideoPlayer url={item.key} />
            if(item.props.children.type === "img") {
                return <img src={item.props.children.props.src} alt=""/>;
            }
            else {
                return  <div className="video-thumbnail">
                    <video controls={false} preload="metadata">
                        <source  src={item.props.children.props.src} type="video/mp4"/>
                    </video>   
                </div>
        }
    });

    return(
        <div className={`image-viewer-fullscreen${showControls ? '' : ' controls-hidden'}`}>
            {DEBUG_FULLSCREEN_VIEWER &&
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 999999,
                background: 'rgba(0,0,0,0.75)',
                color: '#0f0',
                fontSize: '11px',
                lineHeight: '1.4',
                padding: '4px 6px',
                whiteSpace: 'pre-wrap',
                pointerEvents: 'none',
                fontFamily: 'monospace'
            }}>
                {`rest:  ${debug.rest ?? '-'}\nstart: ${debug.start ?? '-'}\nend:   ${debug.end ?? '-'}`}
            </div>}
            <div className='top-bar'>
                <div className='icon-container' onClick={() => props.close()}>
                    <Icon className="close" size={2} path={mdiCloseCircle} />
                </div>
            </div>
           <div
                className="main-image-container"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEndOrCancel}
                onTouchCancel={handleTouchEndOrCancel}
           >
                <Carousel 
                    autoPlay={false}
                    infiniteLoop={true}
                    swipeable={false}
                    renderArrowPrev={prevArrow}
                    renderArrowNext={nextArrow}
                    showIndicators={false}
                    //onClickThumb={() => console.log('clicked on thumbnail')}
                    showStatus={false}
                    useKeyboardArrows={true}
                    showArrows={true}
                    selectedItem={mainIndex}
                    onChange={(index:any) => setMainIndex(index)}
                    showThumbs={false}
                    //centerMode={true}
                    //centerSlidePercentage={100}
                    //dynamicHeight={true}
                    //renderThumbs={customRenderThumb}
                >
                    {props.images.map((media, index) => {
                        return(
                            <div key={media.data} className="main-image">
                                {media.fileType === 'image' ?
                                    <img alt='this is an alt' src={media.data} />
                                : 
                                <DisplayVideo index={index} mainIndex={mainIndex} src={media.data}/>
                                }
                            </div>
                        );
                    })}
                </Carousel>
            </div>
        </div>
    );
};

function DisplayVideo(props:any) {
    const ref = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        if(props.index !== props.mainIndex) {
            pause();
        }
    }, [props.mainIndex, props.index]);

    const pause = () => {
        if(ref.current) {
            ref.current.pause();
        }
    }

    return (
        <div className="video-container">
            <video 
                ref={ref} 
                controls={true} 
                preload="metadata"
                style={{
                    maxWidth: '100%',
                    maxHeight: '100vh',
                    width: 'auto',
                    height: 'auto'
                }}
                playsInline
            >
                <source src={props.src} type="video/mp4"/>
                Your browser does not support the video tag.
            </video>
        </div>
    );
}
 
function prevArrow(prev:any) {
    return(
        <div onClick={prev}>
            <Icon style={{zIndex:1000}} className="left" size={2} path={mdiChevronLeftCircle} />
        </div>
    )
}

function nextArrow(next:any) {
    return(
        <div onClick={next}>
            <Icon className="right" size={2} path={mdiChevronRightCircle} />
        </div>
    )
}